import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { GameResult } from "@topzle/types";
import { z } from "zod";

const questions = [
  {
    id: "q1",
    text: "Which planet is closest to the Sun?",
    choices: ["Venus", "Mercury", "Mars", "Earth"],
    answer: 1,
    source: "NASA Solar System Exploration",
  },
  {
    id: "q2",
    text: "How many sides does a regular hexagon have?",
    choices: ["5", "6", "7", "8"],
    answer: 1,
    source: "Euclidean geometry",
  },
  {
    id: "q3",
    text: "What is the chemical symbol for gold?",
    choices: ["Ag", "Au", "Gd", "Go"],
    answer: 1,
    source: "IUPAC periodic table",
  },
];
export const triviaStateSchema = z.object({
  players: z.array(z.string()),
  questionIndex: z.number().int(),
  answers: z.record(z.string(), z.object({ choice: z.number().int(), at: z.number() })),
  scores: z.record(z.string(), z.number()),
  phase: z.enum(["question", "reveal", "finished"]),
  deadline: z.number(),
  revealedAnswer: z.number().nullable(),
});
export const triviaEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("answer"), choice: z.number().int().min(0).max(3) }),
  z.object({ type: z.literal("reveal") }),
  z.object({ type: z.literal("next") }),
  z.object({ type: z.literal("finish") }),
]);
export const triviaSettingsSchema = z.object({
  questionSeconds: z.number().int().min(5).max(90).default(20),
  ranked: z.boolean().default(false),
});
type State = z.infer<typeof triviaStateSchema>;
type Event = z.infer<typeof triviaEventSchema>;
type Settings = z.infer<typeof triviaSettingsSchema>;
export const triviaGame: TopzleGameDefinition<State, Event, Settings, { hasAnswered: boolean }> = {
  id: "trivia",
  version: "1.0.0",
  name: "Trivia",
  shortDescription: "Verified questions, quick answers, team play, and speed scoring.",
  longDescription:
    "Curated factual questions with source metadata; unverified AI content is excluded from ranked play.",
  category: "trivia",
  icon: "brain",
  cover: "trivia",
  minPlayers: 1,
  maxPlayers: 12,
  supportsGuests: true,
  supportsLocalPlay: true,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: true,
  supportsAiPlayers: false,
  supportsLocalAi: true,
  supportsCloudAi: true,
  supportsTournaments: true,
  supportsPersistence: true,
  requiredCapabilities: ["text"],
  supportedLayouts: ["bubbles", "rail", "split", "avatar_only"],
  preferredLayout: "rail",
  stateSchema: triviaStateSchema,
  eventSchema: triviaEventSchema,
  settingsSchema: triviaSettingsSchema,
  createInitialState(context, settings) {
    return {
      players: context.playerIds,
      questionIndex: 0,
      answers: {},
      scores: Object.fromEntries(context.playerIds.map((id) => [id, 0])),
      phase: "question",
      deadline: context.now + settings.questionSeconds * 1000,
      revealedAnswer: null,
    };
  },
  validateEvent(state, event, context) {
    if (event.type === "answer")
      return {
        valid:
          state.phase === "question" &&
          Boolean(context.actorId) &&
          !state.answers[context.actorId ?? ""],
        code: "answer_closed",
        message: "Answers are closed or already submitted",
      };
    return { valid: true };
  },
  reduceEvent(state, event, context) {
    if (event.type === "finish") return { ...state, phase: "finished" };
    const question = questions[state.questionIndex];
    if (event.type === "answer" && context.actorId)
      return {
        ...state,
        answers: { ...state.answers, [context.actorId]: { choice: event.choice, at: context.now } },
      };
    if (event.type === "reveal") {
      const scores = { ...state.scores };
      if (question)
        for (const [id, answer] of Object.entries(state.answers))
          if (answer.choice === question.answer)
            scores[id] =
              (scores[id] ?? 0) +
              Math.max(100, 1000 - Math.floor((answer.at - (state.deadline - 20_000)) / 20));
      return { ...state, scores, phase: "reveal", revealedAnswer: question?.answer ?? null };
    }
    const next = state.questionIndex + 1;
    return next >= questions.length
      ? { ...state, phase: "finished" }
      : {
          ...state,
          questionIndex: next,
          phase: "question",
          answers: {},
          revealedAnswer: null,
          deadline: context.now + 20_000,
        };
  },
  getPublicState: (state) => ({
    ...state,
    question: questions[state.questionIndex]
      ? { ...questions[state.questionIndex], answer: undefined }
      : null,
  }),
  getPrivatePlayerState: (state, id) => ({ hasAnswered: Boolean(state.answers[id]) }),
  getSpectatorState: (state) => ({ ...state, answers: {} }),
  getAvailableActions: (state, id) =>
    state.phase === "question" && !state.answers[id]
      ? [0, 1, 2, 3].map((choice) => ({ type: "answer", choice }))
      : [],
  calculateResult(state): GameResult | null {
    if (state.phase !== "finished") return null;
    const high = Math.max(...Object.values(state.scores));
    return {
      status: "completed",
      winnerIds: Object.entries(state.scores)
        .filter(([, v]) => v === high)
        .map(([id]) => id),
      scores: state.scores,
      reason: "questions_complete",
    };
  },
  handlePlayerJoin(state, arrival) {
    if (
      state.phase === "finished" ||
      state.players.length >= 12 ||
      state.players.includes(arrival.playerId)
    )
      return null;
    return {
      state: {
        ...state,
        players: [...state.players, arrival.playerId],
        scores: { ...state.scores, [arrival.playerId]: 0 },
      },
    };
  },
  handlePlayerDeparture(state, departure) {
    if (!state.players.includes(departure.playerId) || state.players.length <= 1) return null;
    const scores = { ...state.scores };
    const answers = { ...state.answers };
    delete scores[departure.playerId];
    delete answers[departure.playerId];
    return {
      state: {
        ...state,
        players: state.players.filter((id) => id !== departure.playerId),
        scores,
        answers,
      },
    };
  },
};
